/**
 * WerkstattAngebotePage — Angebot-Erstellung + -Verwaltung (Issue #396)
 *
 * Route: /werkstatt/angebote (intern, SV-Büro)
 *
 * Features:
 * - Angebots-Liste mit Status-Filter
 * - Neues Angebot anlegen (Editor mit Kunde-Picker, Fahrzeug-Picker,
 *   Positions-Tabelle, Summen-Anzeige, gueltig_bis, Status-Badge)
 * - Positionen inline bearbeiten, hinzufügen, löschen
 * - Client-seitige Summenberechnung (Netto / MwSt / Brutto)
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FileText, Plus, Search, ArrowLeft, Loader2,
  Trash2, CheckCircle2, AlertCircle, X, Calculator,
  Tag, Car, User, Calendar,
} from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import type { CRMKontakt } from '@/hooks/useCRM';
import {
  useAngebote,
  calcSummen,
  type Angebot,
  type AngebotStatus,
  type LocalPosition,
  type PositionsTyp,
} from '@/hooks/useAngebote';

// ─── Constants ───────────────────────────────────────────────────────────────

const STATUS_LABELS: Record<AngebotStatus, { label: string; bg: string; text: string }> = {
  entwurf:     { label: 'Entwurf',     bg: 'bg-gray-100',   text: 'text-gray-600' },
  versendet:   { label: 'Versendet',   bg: 'bg-blue-50',    text: 'text-blue-700' },
  angenommen:  { label: 'Angenommen',  bg: 'bg-green-50',   text: 'text-green-700' },
  abgelehnt:   { label: 'Abgelehnt',   bg: 'bg-red-50',     text: 'text-red-700' },
  abgelaufen:  { label: 'Abgelaufen',  bg: 'bg-amber-50',   text: 'text-amber-700' },
  in_auftrag:  { label: 'In Auftrag',  bg: 'bg-purple-50',  text: 'text-purple-700' },
};

const EINHEITEN = ['Stk', 'AW', 'h', 'l', 'kg', 'Satz', 'Paar'] as const;

const POSITIONS_TYPEN: { value: PositionsTyp | ''; label: string }[] = [
  { value: '', label: '— Typ —' },
  { value: 'arbeit',     label: 'Arbeit' },
  { value: 'ersatzteil', label: 'Ersatzteil' },
  { value: 'lack',       label: 'Lack' },
  { value: 'material',   label: 'Material' },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatEuro(n: number): string {
  return n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

function formatDate(d: string | null): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function useDebouncedValue<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

// ─── StatusBadge ─────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: AngebotStatus }) {
  const c = STATUS_LABELS[status] ?? { label: status, bg: 'bg-gray-100', text: 'text-gray-600' };
  return (
    <span className={`inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full ${c.bg} ${c.text}`}>
      {c.label}
    </span>
  );
}

// ─── KundePicker ─────────────────────────────────────────────────────────────

interface KundePickerProps {
  value: string | null;
  onChange: (id: string | null, name: string) => void;
}

function KundePicker({ value, onChange }: KundePickerProps) {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<CRMKontakt[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedName, setSelectedName] = useState('');
  const [open, setOpen] = useState(false);
  const debouncedSearch = useDebouncedValue(search, 250);

  useEffect(() => {
    if (!open) return;
    const q = debouncedSearch.trim();
    if (q.length < 2) { setResults([]); return; }
    let cancelled = false;
    setLoading(true);
    (async () => {
      const { data } = await supabase
        .from('crm_kontakte')
        .select('id, name, firma, email, telefon, tags, betrieb, kategorie, favorit, erstellt_am, aktualisiert_am')
        .or(`name.ilike.%${q}%,email.ilike.%${q}%,firma.ilike.%${q}%`)
        .limit(10);
      if (cancelled) return;
      setResults((data || []) as CRMKontakt[]);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [debouncedSearch, open]);

  const select = (k: CRMKontakt) => {
    setSelectedName(k.name);
    setOpen(false);
    setSearch('');
    setResults([]);
    onChange(k.id, k.name);
  };

  const clear = () => {
    setSelectedName('');
    onChange(null, '');
  };

  if (value && selectedName) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm">
        <User className="w-4 h-4 text-gray-400 shrink-0" />
        <span className="flex-1 font-medium text-gray-900">{selectedName}</span>
        <button onClick={clear} className="text-gray-400 hover:text-gray-600">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          value={search}
          onChange={e => { setSearch(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Kunde suchen…"
          aria-label="Kunde suchen"
          className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#B91C1C]/20 focus:border-[#B91C1C] outline-none"
        />
      </div>
      {open && (search.trim().length >= 2) && (
        <div className="absolute z-20 top-full mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {loading ? (
            <div className="px-4 py-3 flex items-center gap-2 text-sm text-gray-400">
              <Loader2 className="w-4 h-4 animate-spin" /> Suche…
            </div>
          ) : results.length === 0 ? (
            <div className="px-4 py-3 text-sm text-gray-400">Keine Treffer</div>
          ) : results.map(k => (
            <button
              key={k.id}
              onMouseDown={() => select(k)}
              className="w-full text-left px-4 py-2.5 hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-0"
            >
              <p className="text-sm font-medium text-gray-900">{k.name}</p>
              {k.firma && <p className="text-xs text-gray-500">{k.firma}</p>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── FahrzeugPicker ───────────────────────────────────────────────────────────

interface FahrzeugPickerProps {
  value: string | null;
  onChange: (id: string | null, label: string) => void;
}

function FahrzeugPicker({ value, onChange }: FahrzeugPickerProps) {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<Array<{ id: string; kennzeichen: string; marke?: string; modell?: string }>>([]);
  const [loading, setLoading] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState('');
  const [open, setOpen] = useState(false);
  const debouncedSearch = useDebouncedValue(search, 250);

  useEffect(() => {
    if (!open) return;
    const q = debouncedSearch.trim();
    if (q.length < 2) { setResults([]); return; }
    let cancelled = false;
    setLoading(true);
    (async () => {
      const { data } = await supabase
        .from('fahrzeuge')
        .select('id, kennzeichen, marke, modell')
        .or(`kennzeichen.ilike.%${q}%,marke.ilike.%${q}%,modell.ilike.%${q}%`)
        .limit(10);
      if (cancelled) return;
      setResults(data || []);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [debouncedSearch, open]);

  const select = (f: { id: string; kennzeichen: string; marke?: string; modell?: string }) => {
    const label = [f.kennzeichen, f.marke, f.modell].filter(Boolean).join(' · ');
    setSelectedLabel(label);
    setOpen(false);
    setSearch('');
    setResults([]);
    onChange(f.id, label);
  };

  const clear = () => {
    setSelectedLabel('');
    onChange(null, '');
  };

  if (value && selectedLabel) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm">
        <Car className="w-4 h-4 text-gray-400 shrink-0" />
        <span className="flex-1 font-medium text-gray-900">{selectedLabel}</span>
        <button onClick={clear} className="text-gray-400 hover:text-gray-600">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          value={search}
          onChange={e => { setSearch(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Fahrzeug suchen…"
          aria-label="Fahrzeug suchen"
          className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#B91C1C]/20 focus:border-[#B91C1C] outline-none"
        />
      </div>
      {open && (search.trim().length >= 2) && (
        <div className="absolute z-20 top-full mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {loading ? (
            <div className="px-4 py-3 flex items-center gap-2 text-sm text-gray-400">
              <Loader2 className="w-4 h-4 animate-spin" /> Suche…
            </div>
          ) : results.length === 0 ? (
            <div className="px-4 py-3 text-sm text-gray-400">Keine Treffer</div>
          ) : results.map(f => (
            <button
              key={f.id}
              onMouseDown={() => select(f)}
              className="w-full text-left px-4 py-2.5 hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-0"
            >
              <p className="text-sm font-medium text-gray-900">{f.kennzeichen}</p>
              {(f.marke || f.modell) && (
                <p className="text-xs text-gray-500">{[f.marke, f.modell].filter(Boolean).join(' ')}</p>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── PositionsEditor (inline Tabelle) ────────────────────────────────────────

interface PositionsEditorProps {
  positionen: LocalPosition[];
  onChange: (positionen: LocalPosition[]) => void;
}

function PositionsEditor({ positionen, onChange }: PositionsEditorProps) {
  const add = () => {
    onChange([...positionen, { bezeichnung: '', menge: 1, einheit: 'Stk', einzelpreis_netto: 0, positions_typ: null }]);
  };

  const update = (idx: number, patch: Partial<LocalPosition>) => {
    const next = positionen.map((p, i) => i === idx ? { ...p, ...patch } : p);
    onChange(next);
  };

  const remove = (idx: number) => {
    onChange(positionen.filter((_, i) => i !== idx));
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900">Positionen</h3>
        <button
          type="button"
          onClick={add}
          aria-label="Position hinzufügen"
          className="flex items-center gap-1.5 px-3 py-1.5 bg-[#B91C1C] text-white text-xs font-medium rounded-lg hover:bg-[#991B1B] transition-colors"
        >
          <Plus className="w-3.5 h-3.5" /> Position hinzufügen
        </button>
      </div>

      {positionen.length === 0 ? (
        <div className="border-2 border-dashed border-gray-200 rounded-lg px-4 py-8 text-center">
          <Calculator className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-400">Noch keine Positionen</p>
          <p className="text-xs text-gray-300 mt-1">Klicken Sie auf „Position hinzufügen"</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50 text-left">
                <th className="px-3 py-2 text-xs font-medium text-gray-500 w-[30%] min-w-[140px]">Bezeichnung *</th>
                <th className="px-3 py-2 text-xs font-medium text-gray-500 w-[10%] min-w-[60px]">Menge</th>
                <th className="px-3 py-2 text-xs font-medium text-gray-500 w-[10%] min-w-[60px]">Einheit</th>
                <th className="px-3 py-2 text-xs font-medium text-gray-500 w-[15%] min-w-[90px]">Einzelpreis</th>
                <th className="px-3 py-2 text-xs font-medium text-gray-500 w-[15%] min-w-[80px]">Typ</th>
                <th className="px-3 py-2 text-xs font-medium text-gray-500 w-[12%] text-right">Gesamt</th>
                <th className="px-3 py-2 w-[8%]"></th>
              </tr>
            </thead>
            <tbody>
              {positionen.map((pos, idx) => (
                <tr key={idx} data-testid="position-row" className="border-b border-gray-50 last:border-0">
                  <td className="px-3 py-2">
                    <input
                      value={pos.bezeichnung}
                      onChange={e => update(idx, { bezeichnung: e.target.value })}
                      aria-label="Bezeichnung"
                      placeholder="Beschreibung…"
                      className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:ring-1 focus:ring-[#B91C1C]/30 focus:border-[#B91C1C] outline-none"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={pos.menge}
                      onChange={e => update(idx, { menge: parseFloat(e.target.value) || 0 })}
                      aria-label="Menge"
                      className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm text-right focus:ring-1 focus:ring-[#B91C1C]/30 focus:border-[#B91C1C] outline-none"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <select
                      value={pos.einheit}
                      onChange={e => update(idx, { einheit: e.target.value })}
                      aria-label="Einheit"
                      className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:ring-1 focus:ring-[#B91C1C]/30 focus:border-[#B91C1C] outline-none"
                    >
                      {EINHEITEN.map(e => <option key={e} value={e}>{e}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={pos.einzelpreis_netto}
                      onChange={e => update(idx, { einzelpreis_netto: parseFloat(e.target.value) || 0 })}
                      aria-label="Einzelpreis"
                      className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm text-right focus:ring-1 focus:ring-[#B91C1C]/30 focus:border-[#B91C1C] outline-none"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <select
                      value={pos.positions_typ ?? ''}
                      onChange={e => update(idx, { positions_typ: (e.target.value as PositionsTyp) || null })}
                      aria-label="Positions-Typ"
                      className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:ring-1 focus:ring-[#B91C1C]/30 focus:border-[#B91C1C] outline-none"
                    >
                      {POSITIONS_TYPEN.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2 text-right text-sm text-gray-900 font-medium whitespace-nowrap">
                    {formatEuro(Math.round(pos.menge * pos.einzelpreis_netto * 100) / 100)}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => remove(idx)}
                      aria-label="Position löschen"
                      className="p-1.5 text-gray-400 hover:text-red-600 rounded transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── SummenAnzeige ────────────────────────────────────────────────────────────

function SummenAnzeige({ positionen, mwstSatz }: { positionen: LocalPosition[]; mwstSatz: number }) {
  const { netto, mwstBetrag, brutto } = calcSummen(positionen, mwstSatz);
  return (
    <div className="bg-gray-50 rounded-lg px-4 py-3 space-y-1.5">
      <div className="flex justify-between text-sm text-gray-600">
        <span>Netto</span>
        <span data-testid="summe-netto" className="font-medium">{formatEuro(netto)}</span>
      </div>
      <div className="flex justify-between text-sm text-gray-600">
        <span>MwSt. {mwstSatz} %</span>
        <span>{formatEuro(mwstBetrag)}</span>
      </div>
      <div className="flex justify-between text-sm font-semibold text-gray-900 border-t border-gray-200 pt-1.5">
        <span>Brutto</span>
        <span data-testid="summe-brutto">{formatEuro(brutto)}</span>
      </div>
    </div>
  );
}

// ─── AngebotEditor ────────────────────────────────────────────────────────────

interface AngebotEditorProps {
  betriebId: string;
  onSaved: (a: Angebot) => void;
  onCancel: () => void;
  api: ReturnType<typeof useAngebote>;
}

function AngebotEditor({ betriebId, onSaved, onCancel, api }: AngebotEditorProps) {
  const [kundeId, setKundeId] = useState<string | null>(null);
  const [fahrzeugId, setFahrzeugId] = useState<string | null>(null);
  const [gueltigBis, setGueltigBis] = useState<string>('');
  const [mwstSatz, setMwstSatz] = useState<number>(19);
  const [positionen, setPositionen] = useState<LocalPosition[]>([]);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  const validate = useCallback((): string[] => {
    const errs: string[] = [];
    for (const pos of positionen) {
      if (!pos.bezeichnung.trim()) errs.push('Alle Positionen benötigen eine Bezeichnung.');
    }
    return errs;
  }, [positionen]);

  const handleSave = async () => {
    const errs = validate();
    if (errs.length > 0) { setErrors(errs); return; }
    setErrors([]);
    setSaving(true);
    try {
      const result = await api.create({
        kunde_id: kundeId ?? undefined,
        fahrzeug_id: fahrzeugId ?? undefined,
        mwst_satz: mwstSatz,
        gueltig_bis: gueltigBis || undefined,
      });
      const angebotId = result.angebot.id;

      let failedPositionen = 0;
      for (const pos of positionen) {
        try {
          await api.addPosition(angebotId, {
            bezeichnung: pos.bezeichnung,
            menge: pos.menge,
            einheit: pos.einheit,
            einzelpreis_netto: pos.einzelpreis_netto,
            positions_typ: pos.positions_typ ?? undefined,
          });
        } catch {
          failedPositionen++;
        }
      }

      api.refresh();
      if (failedPositionen > 0) {
        toast.warning(
          `Angebot ${result.angebot.angebot_nr} angelegt, aber ${failedPositionen} Position(en) konnten nicht gespeichert werden. Bitte im Angebot prüfen.`,
        );
      } else {
        toast.success('Angebot gespeichert');
      }
      onSaved(result.angebot);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Fehler beim Speichern';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={onCancel} className="text-gray-400 hover:text-gray-600 p-1 rounded">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h2 className="text-lg font-bold text-gray-900">Neues Angebot</h2>
          <p className="text-xs text-gray-500 mt-0.5">Entwurf · Angebot-Nr. wird automatisch vergeben</p>
        </div>
        <div className="ml-auto">
          <StatusBadge status="entwurf" />
        </div>
      </div>

      {errors.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex gap-2">
          <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
          <ul className="text-sm text-red-700 space-y-0.5">
            {errors.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </div>
      )}

      {/* Stammdaten */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
        <h3 className="text-sm font-semibold text-gray-900">Stammdaten</h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">
              <span className="flex items-center gap-1"><User className="w-3.5 h-3.5" /> Kunde</span>
            </label>
            <KundePicker
              value={kundeId}
              onChange={(id) => setKundeId(id)}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">
              <span className="flex items-center gap-1"><Car className="w-3.5 h-3.5" /> Fahrzeug</span>
            </label>
            <FahrzeugPicker
              value={fahrzeugId}
              onChange={(id) => setFahrzeugId(id)}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">
              <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> Gültig bis</span>
            </label>
            <input
              type="date"
              value={gueltigBis}
              onChange={e => setGueltigBis(e.target.value)}
              aria-label="Gültig bis"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#B91C1C]/20 focus:border-[#B91C1C] outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">
              <span className="flex items-center gap-1"><Tag className="w-3.5 h-3.5" /> MwSt.-Satz (%)</span>
            </label>
            <input
              type="number"
              min="0"
              max="100"
              step="1"
              value={mwstSatz}
              onChange={e => setMwstSatz(parseFloat(e.target.value) || 0)}
              aria-label="MwSt.-Satz"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#B91C1C]/20 focus:border-[#B91C1C] outline-none"
            />
          </div>
        </div>
      </div>

      {/* Positionen */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <PositionsEditor positionen={positionen} onChange={setPositionen} />
      </div>

      {/* Summen */}
      <SummenAnzeige positionen={positionen} mwstSatz={mwstSatz} />

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition-colors"
        >
          Abbrechen
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-1.5 px-5 py-2 bg-[#B91C1C] text-white rounded-lg text-sm font-medium hover:bg-[#991B1B] transition-colors disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
          Entwurf speichern
        </button>
      </div>
    </div>
  );
}

// ─── AngebotRow ───────────────────────────────────────────────────────────────

function AngebotRow({ angebot }: { angebot: Angebot }) {
  const s = STATUS_LABELS[angebot.status] ?? { label: angebot.status, bg: 'bg-gray-100', text: 'text-gray-600' };
  return (
    <tr className="border-b border-gray-50 last:border-0">
      <td className="px-4 py-3 text-sm font-medium text-gray-900">{angebot.angebot_nr}</td>
      <td className="px-4 py-3">
        <span className={`inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full ${s.bg} ${s.text}`}>
          {s.label}
        </span>
      </td>
      <td className="px-4 py-3 text-sm text-gray-600 hidden sm:table-cell">{formatDate(angebot.gueltig_bis)}</td>
      <td className="px-4 py-3 text-sm font-medium text-gray-900 hidden md:table-cell text-right">
        {formatEuro(angebot.brutto_betrag)}
      </td>
      <td className="px-4 py-3 text-sm text-gray-400 hidden lg:table-cell">
        {new Date(angebot.created_at).toLocaleDateString('de-DE')}
      </td>
    </tr>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

type ViewMode = 'list' | 'new';

export default function WerkstattAngebotePage() {
  const [betriebId, setBetriebId] = useState<string | undefined>(undefined);
  const [betriebLoading, setBetriebLoading] = useState(true);
  const [view, setView] = useState<ViewMode>('list');

  // Load betrieb_id for Werkstatt
  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from('betriebe')
        .select('id, typ')
        .eq('typ', 'werkstatt')
        .limit(1)
        .maybeSingle();
      if (data) setBetriebId(data.id);
      setBetriebLoading(false);
    })();
  }, []);

  const api = useAngebote(betriebId);

  const statusOptions: { value: AngebotStatus | 'alle'; label: string }[] = useMemo(() => [
    { value: 'alle', label: 'Alle Status' },
    ...Object.entries(STATUS_LABELS).map(([v, c]) => ({ value: v as AngebotStatus, label: c.label })),
  ], []);

  const handleSaved = useCallback((_a: Angebot) => {
    setView('list');
  }, []);

  if (betriebLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (view === 'new') {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6">
        <AngebotEditor
          betriebId={betriebId!}
          onSaved={handleSaved}
          onCancel={() => setView('list')}
          api={api}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4 px-4 sm:px-0">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Angebote</h1>
          <p className="text-sm text-gray-500 mt-0.5">Werkstatt-Kostenvoranschläge</p>
        </div>
        <button
          onClick={() => setView('new')}
          className="flex items-center gap-1.5 px-4 py-2 bg-[#B91C1C] text-white text-sm font-medium rounded-lg hover:bg-[#991B1B] transition-colors"
        >
          <Plus className="w-4 h-4" /> Neues Angebot
        </button>
      </div>

      {/* Filter */}
      <div className="flex gap-3">
        <select
          value={api.statusFilter}
          onChange={e => api.setStatusFilter(e.target.value as AngebotStatus | 'alle')}
          aria-label="Status-Filter"
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-[#B91C1C]/20 focus:border-[#B91C1C] outline-none"
        >
          {statusOptions.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {/* List */}
      {api.loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      ) : api.error ? (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 shrink-0" />
          <p className="text-sm text-red-700">{api.error}</p>
        </div>
      ) : api.angebote.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 px-6 py-14 text-center">
          <FileText className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500">Keine Angebote vorhanden</p>
          <button
            onClick={() => setView('new')}
            className="mt-3 text-sm text-[#B91C1C] hover:underline flex items-center gap-1 mx-auto"
          >
            <Plus className="w-3.5 h-3.5" /> Erstes Angebot anlegen
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                <th className="text-left text-xs font-medium text-gray-500 px-4 py-3">Angebot-Nr.</th>
                <th className="text-left text-xs font-medium text-gray-500 px-4 py-3">Status</th>
                <th className="text-left text-xs font-medium text-gray-500 px-4 py-3 hidden sm:table-cell">Gültig bis</th>
                <th className="text-right text-xs font-medium text-gray-500 px-4 py-3 hidden md:table-cell">Brutto</th>
                <th className="text-left text-xs font-medium text-gray-500 px-4 py-3 hidden lg:table-cell">Erstellt</th>
              </tr>
            </thead>
            <tbody>
              {api.angebote.map(a => (
                <AngebotRow key={a.id} angebot={a} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
